<template>
  <div class="mb-8">
    <v-row class="my-0">
      <v-col cols="12" class="py-0">
        <HeadingBox icon="mdi-bell" title="Indkaldelse" :loading="callUpLogsLoading"/>
      </v-col>
    </v-row>
    <v-row class="my-0">
      <v-col cols="12" lg="5" class="py-0 pl-3 pl-md-16 pl-lg-16 mb-4 mb-lg-0">
        <v-alert v-if="!callUpLogsLoading && (visitItem['callUpStatus'] === 1 || visitItem['callUpStatus'] === 2 || visitItem['callUpStatus'] === 4)" dense text border="left" type="info" :color="callupStatusColor">{{ callupStatusText }}</v-alert>
        <template v-if="callUpLogs.length > 0">
          <div v-for="logItem in callUpLogs" class="mb-2 d-flex flex-wrap align-center">
            <v-tooltip bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="20" class="mr-2">{{ typeIcon(logItem['incallType']) }}</v-icon>
              </template>
              <span>{{ typeText(logItem['incallType']) }}</span>
            </v-tooltip>
            <v-tooltip bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="20" :class="['mr-4', logStatusIconColor(logItem['callUpStatus'])]">{{ logStatusIconName(logItem['callUpStatus']) }}</v-icon>
              </template>
              <span>{{ logStatusText(logItem['callUpStatus']) }}</span>
            </v-tooltip>
            <span class="text-body-1 mr-4 text-no-wrap">{{ formattedDate(logItem['incallDate']) }}</span>
            <v-tooltip v-if="logItem['pdfFileName']" bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click.prevent="downloadCallUpFile(logItem['visitLogId'])">
                  <v-icon size="22">icon-pdf</v-icon>
                </v-btn>
              </template>
              <span>Download</span>
            </v-tooltip>
            <v-tooltip v-if="logItem['incallContent']" bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="openPreviewDialog(logItem['incallContent'])">
                  <v-icon size="20">mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Forhåndsvisning</span>
            </v-tooltip>
            <EboksAttachment
              v-for="attachment in (logItem['eboksMessageAttachments'] || [])"
              :key="`callup-${logItem['visitLogId']}-${attachment['id']}`"
              :visit-id="visitItem.id"
              :attachment-id="attachment['id']"
              :attachment-type="attachment['type']"
            />
          </div>
        </template>
        <span v-if="callUpLogs.length === 0" class="d-block text-body-1 grey--text">Ingen data...</span>
      </v-col>
      <v-col cols="12" lg="7" class="py-0 pl-3 pl-md-16 pl-lg-3">
        <div class='d-lg-flex justify-lg-end align-center'>
          <v-radio-group hide-details row v-model="callUpType" class="my-0 pt-0 mb-4 mb-lg-0">
            <v-radio class="my-2 mr-10" label="e-Boks" :value="3" color="primary" :disabled='patientDoesntHaveEboks'/>
            <v-radio class="my-2 mr-10" label="SMS" :value="2" color="primary" :disabled='!patientMobilePhone'/>
            <v-radio class="my-2 mr-10" label="Email" :value="0" color="primary" :disabled="!patientEmail"/>
            <v-radio class="my-2 mr-10" label="Brev" :value="1" color="primary"/>
          </v-radio-group>
          <v-btn depressed :color="callUpButtonColor" :loading="callUpSendLoading" :disabled="callUpSendLoading || callUpLogsLoading || (callUpType === null) || visitItem.callUpStatus === 1" @click="sendCallUp">Gensend</v-btn>
        </div>
      </v-col>
    </v-row>
    <v-dialog v-model="previewDialog" width="700" overlay-color="#fff" :overlay-opacity="0.7" @click:outside="closePreviewDialog">
      <v-card>
        <v-card-text class="py-4">
          <div v-html="previewDialogContent" class="pa-3 rounded" style="white-space: pre-line;background-color: rgba(0, 0, 0, 0.05);font-size: 12px; line-height: 2; font-family: monospace, monospace;"/>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="black darken-1" text @click="previewDialog = false; closePreviewDialog()">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import methods from '../../utils/methods';
  import api from '../../api/api';
  import HeadingBox from '../common/HeadingBox';
  import EboksAttachment from '@/components/common/EboksAttachment.vue';
  import logStatusIconMixin from '@/mixins/logStatusIconMixin';

  export default {
    name: 'OverviewCallUp',
    components: {HeadingBox, EboksAttachment},
    mixins: [logStatusIconMixin],

    props: {
      visitItem: {
        type: Object,
      },
      patientEmail: {
        type: String
      },
      patientMobilePhone: {
        type: String
      },
      patientDoesntHaveEboks: {
        type: Boolean
      }
    },

    data: function () {
      return {
        callUpLogsLoading: false,
        callUpLogs: [],
        callUpType: null,
        callUpButtonColor: 'primary',
        callUpSendLoading: false,
        previewDialog: false,
        previewDialogContent: null
      };
    },

    mounted: async function () {
      this.setCallUpType();
      this.getCallUpLogs();
    },

    watch: {
      patientDoesntHaveEboks(newValue, oldValue) {
        this.setCallUpType();
      }
    },

    computed: {
      callupStatusText: function () {
        switch (this.visitItem['callUpStatus']) {
          case 2:
            return 'Nogle incalls har fejl.';
          case 4:
            return 'Indkald ikke mulig pga. hemlig adresse.';
          default:
            return 'Indkaldsbrev er planlagt.';
        }
      },
      callupStatusColor: function () {
        switch (this.visitItem['callUpStatus']) {
          case 2:
            return 'deep-orange accent-4';
          case 4:
            return 'deep-orange accent-4';
          default:
            return 'grey darken-2';
        }
      },
    },

    methods: {
      openPreviewDialog: function (value) {
        this.previewDialogContent = value;
        this.previewDialog = true;
      },
      closePreviewDialog: function () {
        this.previewDialog = false;
        setTimeout(() => {
          this.previewDialogContent = null;
        }, 100);
      },
      downloadCallUpFile: function (visitLogId) {
        methods.downloadURL(api.incallLogsFileUrl(visitLogId));
      },
      formattedDate: function (value) {
        if (value) {
          return`${methods.getDateFromIso(value)}, ${methods.getTimeFromIso(value)}`;
        }
        return 'Afsendelse planlagt..';
      },
      setCallUpLogs: function (data) {
        this.callUpLogs = data.data;
      },
      getCallUpLogs: async function () {
        this.callUpLogsLoading = true;
        let callUpData = await api.getIncallLogs(this.visitItem.id);
        this.setCallUpLogs(callUpData);
        this.callUpLogsLoading = false;
      },
      sendCallUp: async function () {
        try {
          this.callUpButtonColor = 'primary';
          this.callUpSendLoading = true;
          await api.sendCallUp(this.visitItem.id, this.callUpType);
          let newVisit = await api.getVisitInfo(this.visitItem.id);
          this.$emit('updateVisitItem', newVisit.data);
          this.$emit('updateCommonVisitInfo', newVisit.data);
          this.callUpSendLoading = false;
        } catch (e) {
          if (e.response.status === 400) {
            methods.showGlobalSnackbar('error', 'Besøgsopkald via brev er allerede planlagt.');
          }
          this.callUpSendLoading = false;
          this.callUpButtonColor = 'error';
        }
      },
      setCallUpType: function () {
        if (this.patientDoesntHaveEboks && this.patientMobilePhone) {
          this.callUpType = 2;
          return;
        }
        if (this.patientDoesntHaveEboks && this.patientEmail) {
          this.callUpType = 0;
          return;
        }
        if (this.patientDoesntHaveEboks) {
          this.callUpType = 1;
          return;
        }
        if (!this.patientDoesntHaveEboks) {
          this.callUpType = 3;
          return;
        }
        this.callUpType = null;
      },
      typeIcon: function (type) {
        let names = {
          0: 'mdi-at',
          1: 'mdi-email',
          2: 'mdi-message-text',
          3: 'mdi-alpha-e-box'
        };
        return names[type];
      },
      typeText: function (type) {
        let text = {
          0: 'Email',
          1: 'Brev',
          2: 'SMS',
          3: 'e-Boks'
        };
        return text[type];
      },
    },
  };
</script>
